import z from "zod";
import type { MailboxTabular, RfqQuote } from "./RFQ";
import Fetcher from "../Fetcher";
import type { RFQPurchaseOrder } from "./RFQPurchaseOrders";
import type { Mailbox, MailboxCommunication, CommunicationChannel, CommunicationDirection, MailboxEmail, EmailParticipant } from "./Mailbox";

export type RFQMailbox = Mailbox<RFQCommunication> & {
    // Calculated by the backend
    latestQuoteId: number | null;

    // Tables/Quotes for the awards section
    tables: MailboxTabular[];

    // The purchase order sent to this mailbox, if any (at most one per mailbox).
    purchaseOrder?: RFQPurchaseOrder | null;
};

// The RFQ's version of a thread message which includes the quotes extracted from it.
export type RFQCommunication = MailboxCommunication & {
    rfqQuotes: RfqQuote[];
};

// An inbound email that hasn't been assigned to a mailbox yet.
export type RFQUncategorizedCommunication = {
    id: number;
    message: string;
    direction: CommunicationDirection;
    channel: CommunicationChannel;
    createdAt: string;
    updatedAt: string;
    rfqId: number | null;
    vendorRelationshipId: number | null;
    contactId: number | null;
    email: MailboxEmail;
    participants: EmailParticipant[];
};

// Matches purchaser-api
const KnownContactSchema = z.object({
    vendorRelationshipId: z.coerce.number().positive(),
    teamEmailAddressId: z.coerce.number().positive(),
});
export type KnownContact = z.infer<typeof KnownContactSchema>;

// Matches purchaser-api
const AddMailboxPayloadSchema = z.discriminatedUnion("type", [
    KnownContactSchema.extend({
        type: z.literal("relationship"),
    }),
    z.object({
        type: z.literal("email"),
        email: z.string().trim().toLowerCase().email(),
    }),
    z.object({
        type: z.literal("list"),
        contactListId: z.coerce.number().positive(),
    }),
]);
export type AddMailboxPayload = z.infer<typeof AddMailboxPayloadSchema>;
export type AddMailboxParams = { rfqUniquePublicId: string; data?: AddMailboxPayload; batch?: AddMailboxPayload[] };

type AddMailboxOrContactsEdits = {
    mailboxId: number;
    emailAddresses: string[];
}[];

export type DomainMatch = {
    emailAddress: string;
    matchedMailboxId: number;
    vendorName: string | null;
};

type AddMailboxResult = {
    edits: AddMailboxOrContactsEdits;
    domainMatches: DomainMatch[];
};

const f = Fetcher.default();

export default {
    getMailboxes: async (rfqUniquePublicId: string) => {
        return await f.get<RFQMailbox[]>(`/v2/mailboxes?rfqUniquePublicId=${rfqUniquePublicId}`);
    },
    // Only for RFQ-owned mailboxes.
    getMailbox: async (mailboxId: number) => {
        return await f.get<RFQMailbox>(`/v2/mailboxes/${mailboxId}`);
    },
    addMailbox: async (params: AddMailboxParams) => {
        return await f.post<AddMailboxResult>("/v2/mailboxes", { body: params });
    },
    moveToNewThread: async (params: { mailboxId: number; emailAddress: string }) => {
        return await f.post<{ newMailboxId: number }>(`/v2/mailboxes/${params.mailboxId}/move-to-new-thread`, {
            body: { emailAddress: params.emailAddress },
        });
    },
    addStagedEmail: async (params: { mailboxId: number; emailAddress: string }) => {
        return await f.post(`/v2/mailboxes/${params.mailboxId}/staged-emails`, { body: { emailAddress: params.emailAddress } });
    },
    deleteStagedEmail: async (params: { mailboxId: number; emailAddress: string }) => {
        return await f.delete(`/v2/mailboxes/${params.mailboxId}/staged-emails`, { body: { emailAddress: params.emailAddress } });
    },
    bulkAddStagedEmail: async (params: { mailboxIds: number[]; emailAddress: string }) => {
        return await f.post<{ added: number }>("/v2/mailboxes/bulk-staged-email", { body: params });
    },
    bulkRemoveStagedEmail: async (params: { mailboxIds: number[]; emailAddress: string }) => {
        return await f.delete<{ removed: number }>("/v2/mailboxes/bulk-staged-email", { body: params });
    },
    removeAllMailboxes: async (rfqUniquePublicId: string) => {
        // Empty {} body required so Fastify's JSON parser doesn't reject the request
        // (Content-Type defaults to application/json for all calls in this client).
        return await f.delete<{ deletedCount: number }>(`/v2/rfqs/${rfqUniquePublicId}/mailboxes`, { body: {} });
    },
    getRecipientListSummary: async (rfqUniquePublicId: string) => {
        return await f.get<{ knownContactCount: number; unknownEmailCount: number; internalEmailCount: number }>(
            `/v2/rfqs/${rfqUniquePublicId}/recipient-list-summary`,
        );
    },
    createListFromRecipients: async (rfqUniquePublicId: string, body: { name: string; description?: string; includeUnknownEmails: boolean }) => {
        return await f.post<{ id: number; name: string }>(`/v2/rfqs/${rfqUniquePublicId}/contact-list-from-recipients`, { body });
    },
};
